/**
 * Thin fetch wrapper around the MMV Mapper API.
 *
 * - attaches the bearer token
 * - unwraps { data, meta } envelopes
 * - turns API error envelopes into throwable Error objects carrying `code`,
 *   `details` and `status`, so screens can render field-level messages
 */
const BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'mmv.token';

export const tokenStore = {
  get: () => window.sessionStorage.getItem(TOKEN_KEY),
  set: (t) => window.sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => window.sessionStorage.removeItem(TOKEN_KEY),
};

export class ApiClientError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }

async function request(path, { method = 'GET', body, query, isForm = false, signal } = {}) {
  const url = new URL(`${BASE}/api/v1${path}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }

  const headers = {};
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (!isForm && body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(url.toString().replace(window.location.origin, BASE || ''), {
    method,
    headers,
    body: isForm ? body : (body === undefined ? undefined : JSON.stringify(body)),
    signal,
  });

  if (res.status === 204) return null;

  let payload = null;
  const text = await res.text();
  if (text) {
    try { payload = JSON.parse(text); } catch { payload = { raw: text }; }
  }

  if (!res.ok) {
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    const err = payload?.error || {};
    throw new ApiClientError(err.message || `Request failed with status ${res.status}`, {
      status: res.status, code: err.code, details: err.details, requestId: err.requestId,
    });
  }
  return payload;
}

async function downloadFile(path, query, fallbackFilename) {
  const url = new URL(`${BASE}/api/v1${path}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }
  const token = tokenStore.get();
  const res = await fetch(url.toString().replace(window.location.origin, BASE || ''), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let payload = null;
    try { payload = JSON.parse(await res.text()); } catch { /* non-JSON error body */ }
    const err = payload?.error || {};
    throw new ApiClientError(err.message || `Export failed with status ${res.status}`, { status: res.status, code: err.code });
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match ? match[1] : fallbackFilename;
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export const api = {
  /* auth */
  login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
  me: () => request('/auth/me'),
  changePassword: (body) => request('/auth/change-password', { method: 'POST', body }),

  /* insurers */
  listInsurers: (includeInactive = false) => request('/insurers', { query: { includeInactive } }),
  createInsurer: (body) => request('/insurers', { method: 'POST', body }),
  updateInsurer: (id, body) => request(`/insurers/${id}`, { method: 'PATCH', body }),

  /* GIIB master */
  listVehicles: (query) => request('/vehicles', { query }),
  vehicleClasses: () => request('/vehicles/classes'),
  getVehicle: (id) => request(`/vehicles/${id}`),
  createVehicle: (body) => request('/vehicles', { method: 'POST', body }),
  updateVehicle: (id, body) => request(`/vehicles/${id}`, { method: 'PATCH', body }),
  checkDuplicate: (body) => request('/vehicles/check-duplicate', { method: 'POST', body }),

  /* imports */
  listBatches: (query) => request('/imports', { query }),
  getBatch: (id) => request(`/imports/${id}`),
  importFields: () => request('/imports/fields'),
  uploadMaster: (formData) => request('/imports', { method: 'POST', body: formData, isForm: true }),
  stageBatch: (id, columnMap, autoRun = true) => request(`/imports/${id}/stage`, { method: 'POST', body: { columnMap, autoRun } }),
  rematchBatch: (id, onlyUnmatched = false) => request(`/imports/${id}/match`, { method: 'POST', query: { onlyUnmatched } }),

  /* mappings */
  listMappings: (query) => request('/mappings', { query }),
  getMapping: (id) => request(`/mappings/${id}`),
  rescore: (id) => request(`/mappings/${id}/rescore`),
  approve: (id, body) => request(`/mappings/${id}/approve`, { method: 'POST', body }),
  reject: (id, body) => request(`/mappings/${id}/reject`, { method: 'POST', body }),
  remap: (id, body) => request(`/mappings/${id}/remap`, { method: 'POST', body }),
  bulkApprove: (body) => request('/mappings/bulk-approve', { method: 'POST', body }),

  /* registry */
  listRegistry: (query) => request('/registry', { query }),
  getRegistryEntry: (key) => request(`/registry/${key}`),
  exportRegistry: (query) => downloadFile('/registry/export', query, 'mmv-registry-export.xlsx'),

  /* analytics */
  overview: () => request('/analytics/overview'),
  insurerCoverage: () => request('/analytics/insurer-coverage'),
  confidenceDistribution: (insurerId) => request('/analytics/confidence-distribution', { query: { insurerId } }),
  attributeStrength: (insurerId) => request('/analytics/attribute-strength', { query: { insurerId } }),
  disagreements: (limit = 50) => request('/analytics/disagreements', { query: { limit } }),

  /* admin */
  matchConfig: () => request('/admin/match-config'),
  updateMatchConfig: (id, body) => request(`/admin/match-config/${id}`, { method: 'PATCH', body }),
  premiumBands: () => request('/admin/premium-bands'),
  listUsers: () => request('/admin/users'),
  createUser: (body) => request('/admin/users', { method: 'POST', body }),
  updateUser: (id, body) => request(`/admin/users/${id}`, { method: 'PATCH', body }),
  audit: (query) => request('/admin/audit', { query }),
};
